import React, { type ReactNode } from "react";
import { RadialGradient } from "ui/radial-gradient";
import { Flex, Text, type FlexProps } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import { Checklist, ChecklistItem } from "ui/checklist";
import { ArrowLink } from "ui/link";

function ListItem({ children }: { children: ReactNode }) {
  return (
    <ChecklistItem checkColor="grass" fontSize={{ base: "md", lg: "lg" }}>
      {children}
    </ChecklistItem>
  );
}

function TextCard({ backgroundGradient, children, ...props }: { backgroundGradient?: ReactNode } & FlexProps) {
  return (
    <Flex pos="relative" {...props}>
      {backgroundGradient}
      <Flex
        align="start"
        bg="white"
        borderRadius="base"
        border="1px"
        borderColor="base.border"
        p={{ base: 7, md: 10 }}
        direction="column"
        gap={7}
        w="100%"
        h="100%"
        zIndex={1}
      >
        {children}
      </Flex>
    </Flex>
  );
}

export function IntroText() {
  return (
    <Flex direction={{ base: "column", lg: "row" }} gap={5}>
      <TextCard
        flex={{ base: 1, lg: 1.3 }}
        backgroundGradient={
          <>
            <RadialGradient
              variant="cyan"
              top={12}
              right={{ base: 12, lg: 0 }}
              bottom={0}
              left={0}
              transform="rotate(-5deg)"
              opacity={0.6}
            />
            <RadialGradient
              variant="lightspeed"
              top={0}
              right={{ base: 12, lg: 2 }}
              bottom={-4}
              left={-4}
              transform="rotate(10deg)"
              opacity={0.6}
            />
            <RadialGradient variant="mint" top={-8} right={{ base: 4, lg: -2 }} bottom={4} left="10%" opacity={0.8} />
          </>
        }
      >
        <Text maxW={{ base: "58ch", md: "100%", lg: "58ch" }}>
          Customer Data Platforms (CDPs) as we know them are fundamentally broken. When CDPs first grew in popularity,
          data warehouse adoption was extremely low. Now, in {new Date().getFullYear()} any business not leveraging a
          cloud data warehouse is rapidly falling behind.
        </Text>

        <Text textStyle="h7" maxW="32ch">
          Today, there&apos;s a more elegant solution centered around the data warehouse - the Composable CDP.
        </Text>

        <Text maxW={{ base: "58ch", md: "100%", lg: "58ch" }}>
          Unlike traditional CDPs that require duplicative data storage, a Composable CDP takes advantage of your
          existing architecture so you can benefit from the security and flexibility of your own data warehouse while
          spending less. Companies like Warner Music Group, Chime and PetSmart have all adopted a Composable CDP to
          power best-in-class experiences for their customers.
        </Text>

        <ArrowLink href="https://www.linkedin.com/groups/14348481/" target="_blank" variant="grass">
          Join the Composable CDP Community on LinkedIn
        </ArrowLink>
      </TextCard>

      <TextCard
        flex={1}
        backgroundGradient={<RadialGradient variant="mint" top={6} right={{ base: 4, lg: 0 }} boxSize="80%" />}
      >
        <Text as="h2" textStyle="h7">
          What does it mean to be a Composable CDP?
        </Text>

        <Checklist spacing={7} mt={2}>
          <ListItem>The Composable CDP runs on your data infrastructure.</ListItem>

          <ListItem>The Composable CDP can support any schema.</ListItem>

          <ListItem>The Composable CDP is modular, but offers a complete feature set.</ListItem>

          <ListItem>The Composable CDP has unbundled pricing.</ListItem>
        </Checklist>

        <ArrowLink
          href="https://cdn.sanity.io/files/pwmfmi47/production/c18ce8f60108fc7c2cad378b78a72ec5c7cc3c25.pdf"
          variant="grass"
          target="_blank"
          size={{ base: "md", lg: "lg" }}
          mt={4}
          onClick={() => {
            trackEvent("Resource Clicked", {
              file: "CDP vs. Composable CDP Comparison Chart",
            });
          }}
        >
          Read more
        </ArrowLink>
      </TextCard>
    </Flex>
  );
}
